import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useShipmentsStore } from '../store/shipmentsStore';
import CustomDropdown from './CustomDropdown';
import { useDebounce } from 'use-debounce';

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 16px;
    row-gap: 16px;
  }
`;

const FilterContainer = styled.div`
  width: 100%;

  @media (min-width: 768px) {
    width: 180px;
    flex-shrink: 0;
  }

  @media (min-width: 1200px) {
    width: 200px;
  }
`;

const PriceContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (min-width: 768px) {
    width: 280px;
    flex-shrink: 0;
  }

  @media (min-width: 1200px) {
    width: 450px;
  }
`;

const PriceInputsWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PriceInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.04);
  color: var(--color-text);
  font-family: var(--font-family-main);
  font-size: 14px;
  line-height: 1.5;
  min-height: 40px;
  transition: all 0.2s ease;
  
  /* Скрываем стандартные стрелки */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const PriceSeparator = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-family: var(--font-family-main);
`;

const Label = styled.label`
  font-family: var(--font-family-main);
  font-weight: 400;
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
  display: block;
`;

const CompactDropdownWrapper = styled.div`
  /* Переопределяем стили дропдауна для более компактного вида */
  button {
    padding: 8px 12px !important;
    font-size: 14px !important;
    min-height: 40px !important;
  }
  
  label {
    font-size: 12px !important;
  }
`;

const DaysContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (min-width: 768px) {
    width: 200px;
    flex-shrink: 0;
  }

  @media (min-width: 1200px) {
    width: 220px;
  }
`;

const DaysInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.04);
  color: var(--color-text);
  font-family: var(--font-family-main);
  font-size: 14px;
  line-height: 1.5;
  min-height: 40px;
  transition: all 0.2s ease;
  
  /* Скрываем стандартные стрелки */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ClearButtonWrapper = styled.div`
  width: 100%;
  
  @media (min-width: 768px) {
    width: auto;
    display: flex;
    align-items: flex-end;
  }
`;

const ClearButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid #FF6B6B;
  background-color: transparent;
  color: #FF6B6B;
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-height: 40px;
  width: 100%;
  
  @media (min-width: 768px) {
    width: auto;
  }
  
  &:hover {
    background-color: #FF6B6B;
    color: #FFFFFF;
  }
`;

const ShipmentsFilters: React.FC = () => {
  const {
    selectedArticleId,
    selectedModelId,
    priceFrom,
    priceTo,
    daysOnShipment,
    selectedShipmentStatusId,
    shipmentStatuses,
    availableArticles,
    availableModels,
    isLoadingArticles,
    isLoadingModels,
    setSelectedArticleId,
    setSelectedModelId,
    setPriceFrom,
    setPriceTo,
    setDaysOnShipment,
    setSelectedShipmentStatusId,
    fetchAllArticles,
    fetchAllModels,
    fetchShipmentStatuses,
  } = useShipmentsStore();

  const [localPriceFrom, setLocalPriceFrom] = useState(priceFrom?.toString() || '');
  const [localPriceTo, setLocalPriceTo] = useState(priceTo?.toString() || '');
  const [localDaysOnShipment, setLocalDaysOnShipment] = useState(daysOnShipment?.toString() || '');
  const [isFirstRender, setIsFirstRender] = useState(true);

  // Debounced значения для цены и дней (задержка 800мс)
  const [debouncedPriceFrom] = useDebounce(priceFrom, 800);
  const [debouncedPriceTo] = useDebounce(priceTo, 800);
  const [debouncedDaysOnShipment] = useDebounce(daysOnShipment, 800);

  useEffect(() => {
    setLocalPriceFrom(priceFrom?.toString() || '');
  }, [priceFrom]);

  useEffect(() => {
    setLocalPriceTo(priceTo?.toString() || '');
  }, [priceTo]);

  useEffect(() => {
    setLocalDaysOnShipment(daysOnShipment?.toString() || '');
  }, [daysOnShipment]);

  // Загружаем все данные для фильтров при монтировании компонента
  useEffect(() => {
    const loadFilterData = async () => {
      await Promise.all([
        fetchAllArticles(),
        fetchAllModels(),
        fetchShipmentStatuses()
      ]);
    };

    loadFilterData();
  }, [fetchAllArticles, fetchAllModels, fetchShipmentStatuses]);

  const handlePriceFromChange = (value: string) => {
    // Запрещаем отрицательные значения
    if (value && parseInt(value) < 0) return;
    
    setLocalPriceFrom(value);
    const numValue = value ? parseInt(value) : null;
    setPriceFrom(numValue);
  };

  const handlePriceToChange = (value: string) => {
    // Запрещаем отрицательные значения
    if (value && parseInt(value) < 0) return;
    
    setLocalPriceTo(value);
    const numValue = value ? parseInt(value) : null;
    setPriceTo(numValue);
  };

  const handleDaysOnShipmentChange = (value: string) => {
    // Запрещаем отрицательные значения
    if (value && parseInt(value) < 0) return;
    
    setLocalDaysOnShipment(value);
    const numValue = value ? parseInt(value) : null;
    setDaysOnShipment(numValue);
  };

  // Подготовка опций для дропдаунов
  const articleOptions = [
    { value: "", label: isLoadingArticles ? "Загрузка..." : "Все артикулы" },
    ...(availableArticles?.map((article) => ({
      value: article,
      label: article,
    })) || []),
  ];

  const modelOptions = [
    { value: "", label: isLoadingModels ? "Загрузка..." : "Все модели" },
    ...(availableModels?.map((model) => ({
      value: model,
      label: model,
    })) || []),
  ];

  const statusOptions = [
    { value: "", label: "Все статусы" },
    ...(shipmentStatuses?.map((status) => ({
      value: status.id.toString(),
      label: status.status,
    })) || []),
  ];

  const handleArticleChange = (value: string) => {
    setSelectedArticleId(value || null);
  };

  const handleModelChange = (value: string) => {
    setSelectedModelId(value || null);
  };

  const handleStatusChange = (value: string) => {
    setSelectedShipmentStatusId(value ? parseInt(value) : null);
  };

  // Автоматическое применение фильтров при изменении
  useEffect(() => {
    // Пропускаем первый рендер
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    // Применяем фильтры без глобального лоадера
    useShipmentsStore.getState().applyFilters();
  }, [selectedArticleId, selectedModelId, debouncedPriceFrom, debouncedPriceTo, debouncedDaysOnShipment, selectedShipmentStatusId]);

  const handleClearFilters = () => {
    const { clearFilters } = useShipmentsStore.getState();
    clearFilters();
    setLocalPriceFrom('');
    setLocalPriceTo('');
    setLocalDaysOnShipment('');
  };

  return (
    <FiltersContainer>
      <FilterContainer>
        <CompactDropdownWrapper>
          <CustomDropdown
            options={articleOptions}
            value={selectedArticleId || ""}
            placeholder="Выберите артикул"
            onChange={handleArticleChange}
            label="Артикул"
            disabled={isLoadingArticles}
          />
        </CompactDropdownWrapper>
      </FilterContainer>

      <FilterContainer>
        <CompactDropdownWrapper>
          <CustomDropdown
            options={modelOptions}
            value={selectedModelId || ""}
            placeholder="Выберите модель"
            onChange={handleModelChange}
            label="Модель"
            disabled={isLoadingModels}
          />
        </CompactDropdownWrapper>
      </FilterContainer>

      <FilterContainer>
        <CompactDropdownWrapper>
          <CustomDropdown
            options={statusOptions}
            value={selectedShipmentStatusId?.toString() || ""}
            placeholder="Выберите статус"
            onChange={handleStatusChange}
            label="Статус отгрузки"
          />
        </CompactDropdownWrapper>
      </FilterContainer>

      <DaysContainer>
        <Label>Кол-во дней в отгрузке</Label>
        <DaysInput
          type="number"
          placeholder="Кол-во"
          value={localDaysOnShipment}
          onChange={(e) => handleDaysOnShipmentChange(e.target.value)}
          min="0"
        />
      </DaysContainer>

      <PriceContainer>
        <Label>Цена</Label>
        <PriceInputsWrapper>
          <PriceInput
            type="number"
            placeholder="От"
            value={localPriceFrom}
            onChange={(e) => handlePriceFromChange(e.target.value)}
            min="0"
          />
          <PriceSeparator>—</PriceSeparator>
          <PriceInput
            type="number"
            placeholder="До"
            value={localPriceTo}
            onChange={(e) => handlePriceToChange(e.target.value)}
            min="0"
          />
        </PriceInputsWrapper>
      </PriceContainer>

      <ClearButtonWrapper>
        <ClearButton onClick={handleClearFilters}>
          Сбросить
        </ClearButton>
      </ClearButtonWrapper>
    </FiltersContainer>
  );
};

export default ShipmentsFilters;
